/**
 * Staff attendance API + TanStack Query hooks (Prompt 11E.6).
 *
 * Endpoints:
 *   GET  /v1/staff/{staff_id}/attendance?year=&month=   — derived+manual month view
 *   PUT  /v1/staff/{staff_id}/attendance/{date}          — admin manual mark
 *   GET  /v1/staff-attendance/summary?year=&month=       — admin all-staff summary
 *   GET  /v1/my/attendance?year=&month=                  — self-service: own month view
 *   POST /v1/my/attendance/override-requests              — self-service: request an override
 *   GET  /v1/my/attendance/override-requests              — self-service: own requests
 *   GET  /v1/staff-attendance/override-requests            — admin queue
 *   POST /v1/staff-attendance/override-requests/{id}/approve|deny
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@core/api/client';

// Hand-declared — see the comment in ./leaves.ts for why these aren't
// sourced from the (currently stale) generated `components['schemas']`.
// Matches backend/app/features/attendance/schemas.py exactly.
export type StaffDayStatus = 'present' | 'absent';

export interface StaffDayAttendance {
  date: string;
  status: StaffDayStatus;
  source: 'derived' | 'manual';
  notes?: string | null;
}

export interface StaffAttendanceMonthResponse {
  staff_id: string;
  year: number;
  month: number;
  days: StaffDayAttendance[];
  present_days: number;
  absent_days: number;
  attendance_pct: number | null;
}

export interface StaffAttendanceSummaryRow {
  staff_id: string;
  staff_name: string;
  present_days: number;
  absent_days: number;
  attendance_pct: number | null;
  leave_balance: number | null;
}

export interface StaffAttendanceSummaryResponse {
  year: number;
  month: number;
  rows: StaffAttendanceSummaryRow[];
}

export interface StaffAttendanceMarkRequest {
  status: StaffDayStatus;
  notes?: string;
}

export type OverrideRequestStatus = 'pending' | 'approved' | 'denied';

export interface StaffAttendanceOverrideRequest {
  id: string;
  staff_id: string;
  date: string;
  requested_status: StaffDayStatus;
  note: string | null;
  status: OverrideRequestStatus;
  requested_by_uid: string;
  decided_by_uid: string | null;
  decided_at: string | null;
  decision_note: string | null;
  created_at: string;
  updated_at: string;
}

export interface StaffAttendanceOverrideRequestListResponse {
  items: StaffAttendanceOverrideRequest[];
  total: number;
}

export interface StaffAttendanceOverrideRequestCreate {
  date: string;
  requested_status: StaffDayStatus;
  note?: string;
}

export interface StaffAttendanceOverrideDecision {
  note?: string;
}

export const staffAttendanceKeys = {
  month: (staffId: string, year: number, month: number) =>
    ['staff-attendance', staffId, year, month] as const,
  myMonth: (year: number, month: number) => ['staff-attendance', 'my', year, month] as const,
  summary: (year: number, month: number) => ['staff-attendance', 'summary', year, month] as const,
  myOverrideRequests: () => ['staff-attendance', 'override-requests', 'my'] as const,
  overrideRequestQueue: (status?: string) =>
    ['staff-attendance', 'override-requests', 'queue', status ?? 'all'] as const,
};

export function useStaffAttendanceMonth(
  staffId: string | undefined,
  year: number,
  month: number,
) {
  return useQuery({
    queryKey: staffAttendanceKeys.month(staffId ?? '__none__', year, month),
    queryFn: () =>
      api.get<StaffAttendanceMonthResponse>(
        `/v1/staff/${staffId}/attendance?year=${year}&month=${month}`,
      ),
    enabled: Boolean(staffId),
  });
}

export function useMarkStaffAttendance(staffId: string, year: number, month: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ date, body }: { date: string; body: StaffAttendanceMarkRequest }) =>
      api.put<StaffDayAttendance>(`/v1/staff/${staffId}/attendance/${date}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staffAttendanceKeys.month(staffId, year, month) });
      qc.invalidateQueries({ queryKey: staffAttendanceKeys.summary(year, month) });
    },
  });
}

export function useStaffAttendanceSummary(year: number, month: number) {
  return useQuery({
    queryKey: staffAttendanceKeys.summary(year, month),
    queryFn: () =>
      api.get<StaffAttendanceSummaryResponse>(
        `/v1/staff-attendance/summary?year=${year}&month=${month}`,
      ),
  });
}

// ---------------------------------------------------------------------------
// Self-service — /v1/my/attendance
// ---------------------------------------------------------------------------

export function useMyAttendanceMonth(year: number, month: number) {
  return useQuery({
    queryKey: staffAttendanceKeys.myMonth(year, month),
    queryFn: () =>
      api.get<StaffAttendanceMonthResponse>(`/v1/my/attendance?year=${year}&month=${month}`),
  });
}

export function useMyOverrideRequests() {
  return useQuery({
    queryKey: staffAttendanceKeys.myOverrideRequests(),
    queryFn: () =>
      api.get<StaffAttendanceOverrideRequestListResponse>('/v1/my/attendance/override-requests'),
  });
}

export function useRequestOverride(year: number, month: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: StaffAttendanceOverrideRequestCreate) =>
      api.post<StaffAttendanceOverrideRequest>('/v1/my/attendance/override-requests', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staffAttendanceKeys.myOverrideRequests() });
      qc.invalidateQueries({ queryKey: staffAttendanceKeys.myMonth(year, month) });
    },
  });
}

// ---------------------------------------------------------------------------
// Admin — override-request approval queue
// ---------------------------------------------------------------------------

export function useOverrideRequestQueue(status?: OverrideRequestStatus | '') {
  return useQuery({
    queryKey: staffAttendanceKeys.overrideRequestQueue(status || undefined),
    queryFn: () =>
      api.get<StaffAttendanceOverrideRequestListResponse>(
        `/v1/staff-attendance/override-requests${status ? `?status=${status}` : ''}`,
      ),
  });
}

function useDecideOverrideRequest(action: 'approve' | 'deny') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: StaffAttendanceOverrideDecision }) =>
      api.post<StaffAttendanceOverrideRequest>(
        `/v1/staff-attendance/override-requests/${id}/${action}`,
        body,
      ),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['staff-attendance', 'override-requests'] });
      qc.invalidateQueries({ queryKey: ['staff-attendance', result.staff_id] });
      qc.invalidateQueries({ queryKey: ['staff-attendance', 'summary'] });
    },
  });
}

export function useApproveOverrideRequest() {
  return useDecideOverrideRequest('approve');
}

export function useDenyOverrideRequest() {
  return useDecideOverrideRequest('deny');
}
